import { useEffect } from 'react'
import * as socketService from '@/services/socket'
import { useAuthStore } from '@/stores/authStore'
import { useDealsStore } from '@/stores/useDealsStore'
import { mapDealFromApi } from '@/adapters/deals'

export const NOTIFICATIONS_REFRESH_EVENT = 'crm:notifications-refresh'

export function useDealSocket() {
  const upsertDeal = useDealsStore((s) => s.upsertDeal)
  const updateDealById = useDealsStore((s) => s.updateDealById)
  const removeDealById = useDealsStore((s) => s.removeDealById)

  useEffect(() => {
    const handleCreated = (payload: { deal: unknown }) => {
      if (!payload?.deal) return
      const deal = mapDealFromApi(payload.deal)
      upsertDeal(deal)
    }

    const handleUpdated = (payload: { deal: unknown }) => {
      if (!payload?.deal) return
      const deal = mapDealFromApi(payload.deal)
      const existing = useDealsStore.getState().deals.find((d) => d.id === deal.id)
      if (existing?.updatedAt && deal.updatedAt && existing.updatedAt === deal.updatedAt) {
        return
      }
      updateDealById(deal.id, deal)
      const userId = useAuthStore.getState().user?.id
      if (deal.financeAwaitingCloser && userId && deal.closerId === userId) {
        window.dispatchEvent(new CustomEvent(NOTIFICATIONS_REFRESH_EVENT))
      }
    }

    const handleStageChanged = (payload: { deal: unknown }) => {
      handleUpdated(payload)
    }

    const handleDeleted = (payload: { dealId: string }) => {
      if (!payload?.dealId) return
      removeDealById(payload.dealId)
    }

    const tryConnect = () => {
      const { authReady, token } = useAuthStore.getState()
      if (!authReady || !token) return
      socketService.connect()
    }

    tryConnect()

    const unsubAuth = useAuthStore.subscribe((state, prev) => {
      if (!state.authReady) return
      if (state.token && state.token !== prev.token) {
        socketService.reconnect()
        return
      }
      if (state.authReady && !prev.authReady && state.token) {
        socketService.connect()
      }
    })

    socketService.on('deal:created', handleCreated)
    socketService.on('deal:updated', handleUpdated)
    socketService.on('deal:stage-changed', handleStageChanged)
    socketService.on('deal:deleted', handleDeleted)

    return () => {
      unsubAuth()
      socketService.off('deal:created', handleCreated)
      socketService.off('deal:updated', handleUpdated)
      socketService.off('deal:stage-changed', handleStageChanged)
      socketService.off('deal:deleted', handleDeleted)
      socketService.disconnect()
    }
  }, [upsertDeal, updateDealById, removeDealById])
}
